import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckOutlined as CheckIcon,
  LinkOutlined as LinkIcon,
  PictureAsPdfOutlined as PdfIcon,
  SearchOutlined as SearchIcon,
  StarRounded as StarIcon,
  ViewAgendaOutlined as CardsIcon,
  ViewSidebarOutlined as PanelIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../../context/auth-context';
import { PageHeader } from '../../components/shared';

const LIMIT = 20;

// ── Shared sub-components ─────────────────────────────────────────────────────

const ScoreBar = ({ label, value, pct, weight }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}{' '}
        {weight != null && (
          <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
            ({Math.round(weight * 100)}%)
          </Typography>
        )}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{Math.round(value ?? 0)}</Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={Math.min(pct ?? 0, 100)}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(40,75,50,0.1)',
        '& .MuiLinearProgress-bar': { backgroundColor: '#284B32', borderRadius: 4 },
      }}
    />
  </Box>
);

const OverallBadge = ({ score }) => {
  const color = score >= 80 ? '#1b5e20' : score >= 60 ? '#e65100' : '#b71c1c';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color, lineHeight: 1 }}>
        {score}
      </Typography>
    </Box>
  );
};

const JudgeAvatar = ({ judge, size = 48 }) => {
  const initials = judge.fullname
    ? judge.fullname.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : judge.username?.substring(0, 2).toUpperCase();
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.15)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size > 36 ? '1rem' : '0.75rem', flexShrink: 0,
    }}>
      {initials}
    </Box>
  );
};

const StarRating = ({ value }) => (
  <Box sx={{ display: 'flex', gap: 0.25 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <StarIcon key={n} sx={{ fontSize: '1rem', color: n <= value ? '#e6a817' : 'rgba(0,0,0,0.12)' }} />
    ))}
  </Box>
);

// ── JudgeDetail: the full breakdown content (used in both views) ──────────────

const JudgeDetail = ({ data }) => {
  const { judge, overall, breakdown, weights, events, directorFeedbacks = [] } = data;

  const efectividad = breakdown.efectividad ?? 0;
  const scoreIndex  = breakdown.scoreIndex ?? 0;
  const hasStars    = breakdown.starsAvg != null;
  const starsScore  = hasStars ? (breakdown.starsAvg / 5) * 100 : null;

  return (
    <>
      {/* Header */}
      <Box sx={{ backgroundColor: '#1E3A2F', p: 2.5, borderRadius: '14px 14px 0 0', display: 'flex', alignItems: 'center', gap: 2 }}>
        <JudgeAvatar judge={judge} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            {judge.fullname || judge.username}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' }}>
            {events.length} eventos · {judge.username}
          </Typography>
        </Box>
        <OverallBadge score={overall} />
      </Box>

      <CardContent sx={{ p: 2.5 }}>
        {/* Chips */}
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
          <Tooltip title="Rubrics calificados / slots asignados totales">
            <Chip label={`Efectividad: ${efectividad}%`} size="small" variant="outlined" />
          </Tooltip>
          <Tooltip title="Promedio de (calificación / puntos máximos) × 100">
            <Chip label={`Índice punt.: ${scoreIndex}/100`} size="small" variant="outlined" />
          </Tooltip>
          {hasStars && (
            <Tooltip title="Promedio de estrellas del director">
              <Chip label={`★ Director: ${breakdown.starsAvg.toFixed(1)}`} size="small" variant="outlined" />
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Score bars */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
          Desglose del puntaje overall
        </Typography>
        <ScoreBar label="Efectividad"          value={efectividad} pct={efectividad} weight={null} />
        <ScoreBar label="Índice de puntuación" value={scoreIndex}  pct={scoreIndex}  weight={hasStars ? weights.grades : weights.grades + weights.stars} />
        {hasStars && (
          <ScoreBar label="Estrellas director" value={starsScore}  pct={starsScore}  weight={weights.stars} />
        )}

        {/* Event breakdown */}
        {events.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
              Desglose por evento
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Evento</TableCell>
                    <TableCell align="center">Efectividad</TableCell>
                    <TableCell align="center">Índice punt.</TableCell>
                    <TableCell align="center">Calif. / Slots</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map(ev => (
                    <TableRow key={ev.eventId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.eventName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {ev.efectividad !== null ? `${ev.efectividad}%` : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        {ev.scoreIndex !== null ? `${ev.scoreIndex}/100` : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">{ev.gradedCount} / {ev.totalSlots}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Director feedback */}
        {directorFeedbacks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
              Comentarios del director
            </Typography>
            <Stack spacing={1.5}>
              {directorFeedbacks.map((fb, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(40,75,50,0.05)',
                    border: '1px solid rgba(40,75,50,0.12)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box>
                      {fb.rubricName && (
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#284B32' }}>
                          {fb.rubricName}
                        </Typography>
                      )}
                      {fb.eventName && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', ml: fb.rubricName ? 0.75 : 0 }}>
                          · {fb.eventName}
                        </Typography>
                      )}
                    </Box>
                    <StarRating value={fb.stars} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                    {fb.comment}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </>
  );
};

// ── Card view: scrollable list of cards ──────────────────────────────────────

const JudgeCard = ({ data }) => (
  <Card sx={{ mb: 3 }}>
    <JudgeDetail data={data} />
  </Card>
);

// ── Panel view: left list + right detail ─────────────────────────────────────

const JudgeListItem = ({ data, selected, onClick }) => {
  const { judge, overall, breakdown } = data;
  const color = overall >= 80 ? '#1b5e20' : overall >= 60 ? '#e65100' : '#b71c1c';
  const initials = judge.fullname
    ? judge.fullname.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : judge.username?.substring(0, 2).toUpperCase();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
        borderRadius: 2, cursor: 'pointer',
        backgroundColor: selected ? '#C1EFD0' : 'background.paper',
        border: selected ? '1.5px solid #284B32' : '1.5px solid transparent',
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': { backgroundColor: selected ? '#C1EFD0' : 'action.hover' },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        backgroundColor: selected ? '#284B32' : 'rgba(40,75,50,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: selected ? '#fff' : '#284B32', fontWeight: 700, fontSize: '0.75rem',
      }}>
        {initials}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: selected ? '#284B32' : 'text.primary' }} noWrap>
          {judge.fullname || judge.username}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {breakdown.efectividad ?? 0}% ef. · {breakdown.scoreIndex ?? 0}/100
        </Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color, flexShrink: 0 }}>
        {overall}
      </Typography>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const JudgePerformanceReport = () => {
  const { activeCamp } = useAuthContext();
  const campId = activeCamp?._id;

  const [viewMode, setViewMode]       = useState('panel');
  const [copied, setCopied]           = useState(false);
  const [items, setItems]             = useState([]);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [exporting, setExporting]     = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [selectedJudge, setSelectedJudge] = useState(null);
  // Un 403 aquí solo salía por `console.error` y la página quedaba EN BLANCO: ni datos
  // ni explicación. La ruta ya exige `reports.view-all-zones`, así que este caso queda
  // para cuando el permiso se revoca con la sesión abierta o un `deny` lo quita.
  const [denegado, setDenegado] = useState(false);

  const sentinelRef    = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchedRef     = useRef({ campId: null, search: null, page: null });

  const buildHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchPage = useCallback(async (cId, searchVal, pageNum) => {
    if (!cId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_REST_API}/reports/judge-performance`,
        { params: { campId: cId, search: searchVal || undefined, page: pageNum, limit: LIMIT }, headers: buildHeaders() }
      );
      setItems(prev => {
        const next = pageNum === 1 ? data.results : [...prev, ...data.results];
        if (pageNum === 1 && next.length > 0) {
          setSelectedJudge(prev => prev ?? next[0]);
        }
        return next;
      });
      setHasMore(pageNum < data.totalPages);
    } catch (err) {
      if (err?.response?.status === 403) setDenegado(true);
      else console.error('Error fetching judge performance:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedJudge(null);
  }, [campId, search]);

  useEffect(() => {
    if (!campId) return;
    const ref = fetchedRef.current;
    if (ref.campId === campId && ref.search === search && ref.page === page) return;
    fetchedRef.current = { campId, search, page };
    fetchPage(campId, search, page);
  }, [campId, search, page, fetchPage]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setSearch(val);
    }, 300);
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) setPage(p => p + 1); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading]);

  const handleExportPDF = async () => {
    if (!campId) return;
    setExporting(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_REST_API}/reports/judge-performance`,
        { params: { campId, search: search || undefined, limit: 'all' }, headers: buildHeaders() }
      );
      const doc = new jsPDF();
      doc.setFontSize(14); doc.setTextColor(40, 75, 50);
      doc.text('Rendimiento de Jueces', 14, 16);
      doc.setFontSize(9); doc.setTextColor(100);
      doc.text(`Campamento: ${activeCamp?.name || campId}`, 14, 22);
      autoTable(doc, {
        startY: 28,
        head: [['Juez', 'Usuario', 'Overall', 'Efectividad', 'Índice punt.', '★ Dir.']],
        body: data.map(r => [
          r.judge.fullname || '',
          r.judge.username,
          r.overall,
          `${r.breakdown.efectividad}%`,
          `${r.breakdown.scoreIndex}/100`,
          r.breakdown.starsAvg != null ? r.breakdown.starsAvg.toFixed(1) : '—',
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [40, 75, 50], textColor: 255 },
        alternateRowStyles: { fillColor: [244, 241, 233] },
      });
      doc.save(`rendimiento-jueces-${activeCamp?.name || campId}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
    } finally {
      setExporting(false);
    }
  };

  const isEmpty = !loading && items.length === 0 && campId;

  if (denegado) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="info">
          <AlertTitle>No tienes acceso a este reporte</AlertTitle>
          Rendimiento de Jueces agrega por juez sobre todo el campamento, así que
          necesita el permiso <code>reports.view-all-zones</code>. Pídeselo a un
          administrador si crees que deberías tenerlo.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, height: viewMode === 'panel' ? 'calc(100vh - 80px)' : 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3, flexShrink: 0 }}>
        <PageHeader
          title="Rendimiento de Jueces"
          subtitle="Análisis ponderado: calificaciones (67%) y estrellas del director (33%)."
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {campId && (
            <Tooltip title={copied ? 'Enlace copiado' : 'Copiar enlace público'}>
              <Button
                variant="outlined"
                size="small"
                startIcon={copied ? <CheckIcon /> : <LinkIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/public/judge-performance/${campId}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                sx={{ borderColor: '#284B32', color: '#284B32', '&:hover': { borderColor: '#1E3A2F', backgroundColor: 'rgba(40,75,50,0.05)' } }}
              >
                {copied ? 'Copiado' : 'Compartir'}
              </Button>
            </Tooltip>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => { if (val) setViewMode(val); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5 } }}
          >
            <ToggleButton value="panel">
              <Tooltip title="Vista panel">
                <PanelIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="cards">
              <Tooltip title="Vista tarjetas">
                <CardsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <PdfIcon />}
            onClick={handleExportPDF}
            disabled={!campId || exporting || items.length === 0}
            sx={{ borderColor: '#284B32', color: '#284B32', '&:hover': { borderColor: '#1E3A2F', backgroundColor: 'rgba(40,75,50,0.05)' }, flexShrink: 0 }}
          >
            {exporting ? 'Exportando...' : 'Exportar PDF'}
          </Button>
        </Stack>
      </Box>

      {!campId && (
        <Typography color="text.secondary">Selecciona un campamento para ver el reporte.</Typography>
      )}

      {campId && (
        <TextField
          size="small"
          placeholder="Buscar por nombre o usuario..."
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment> }}
          sx={{ mb: 3, maxWidth: 400, flexShrink: 0 }}
        />
      )}

      {/* ── Panel view ─────────────────────────────────────────────────────── */}
      {campId && viewMode === 'panel' && (
        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          {/* Left: judge list */}
          <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, flexShrink: 0 }}>
                {items.length} jueces{hasMore ? '+' : ''}
              </Typography>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {items.map(item => (
                  <JudgeListItem
                    key={item.judge._id}
                    data={item}
                    selected={selectedJudge?.judge._id === item.judge._id}
                    onClick={() => setSelectedJudge(item)}
                  />
                ))}
                <Box ref={sentinelRef} sx={{ height: 4 }} />
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} sx={{ color: '#284B32' }} />
                  </Box>
                )}
                {isEmpty && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                    {search ? 'Sin resultados.' : 'Sin jueces en este campamento.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right: judge detail */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              {selectedJudge ? (
                <JudgeDetail data={selectedJudge} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.disabled">Selecciona un juez para ver el detalle.</Typography>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Cards view ─────────────────────────────────────────────────────── */}
      {campId && viewMode === 'cards' && (
        <>
          {items.map(item => (
            <JudgeCard key={item.judge._id} data={item} />
          ))}
          <Box ref={sentinelRef} sx={{ height: 4 }} />
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: '#284B32' }} />
            </Box>
          )}
          {isEmpty && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
              {search ? 'Sin resultados para la búsqueda.' : 'Sin jueces en este campamento.'}
            </Typography>
          )}
          {!loading && !hasMore && items.length > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              {items.length} juez/jueces cargados
            </Typography>
          )}
        </>
      )}
    </Container>
  );
};

export default JudgePerformanceReport;
